import { Controller } from '@hotwired/stimulus'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

type TimestampAssociationMap = Record<string, string[]>

interface TimestampValue {
  date: Date
  formatted: string
}

interface DeliveryValidationError {
  deliveryShortcode: string
  message: string
}

interface LateDelivery {
  deliveryShortcode: string
  delivery: TimestampValue
  pickupShortcode: string
  pickup: TimestampValue
}

const TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/
const SEVEN_DAYS_IN_MILLISECONDS = 7 * 24 * 60 * 60 * 1000

export default class extends Controller<HTMLFormElement> {
  static targets = [
    'pickup',
    'delivery',
    'deliveryError',
    'submitButton',
    'lateConfirmationMessage'
  ]

  declare readonly pickupTargets: HTMLInputElement[]
  declare readonly deliveryTargets: HTMLInputElement[]
  declare readonly deliveryErrorTargets: HTMLElement[]
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly lateConfirmationMessageTarget: HTMLElement

  static values = {
    associations: Object,
    timeZone: String
  }

  declare associationsValue: TimestampAssociationMap
  declare readonly timeZoneValue: string

  validate(): boolean {
    const errors = this.hardValidationErrors()
    this.renderErrors(errors)

    return errors.length === 0
  }

  submit(event: SubmitEvent): void {
    if (!this.validate()) {
      this.prevent(event)
      return
    }

    if (this.isConfirmationSubmission(event)) {
      this.cancelConfirmLeave()
      return
    }

    const message = this.lateDeliveryConfirmationMessage()
    if (message == null) {
      this.cancelConfirmLeave()
      return
    }

    this.prevent(event)
    this.renderLateDeliveryConfirmation(message)
    this.submitButtonTarget.click()
  }

  save(event: MouseEvent): void {
    if (!this.validate()) {
      this.prevent(event)
      return
    }

    const message = this.lateDeliveryConfirmationMessage()
    if (message != null) {
      this.renderLateDeliveryConfirmation(message)
      return
    }

    event.preventDefault()
    this.element.requestSubmit()
  }

  private hardValidationErrors(): DeliveryValidationError[] {
    const pickups = this.timestampFieldsByShortcode(this.pickupTargets, 'pickupShortcode')

    return this.timestampFields(this.deliveryTargets).flatMap(deliveryField => {
      const deliveryShortcode = deliveryField.dataset.deliveryShortcode
      if (deliveryShortcode == null || deliveryShortcode === '' || deliveryField.value === '') {
        return []
      }

      const delivery = this.parseTimestamp(deliveryField.value)
      if (delivery == null) {
        return []
      }

      const pickupShortcodes = this.associationsValue[deliveryShortcode] ?? []
      if (pickupShortcodes.length === 0) {
        return []
      }

      const missingPickup = pickupShortcodes.find(shortcode => {
        const pickupField = pickups.get(shortcode)
        return pickupField == null || this.parseTimestamp(pickupField.value) == null
      })

      if (missingPickup !== undefined) {
        return [
          {
            deliveryShortcode,
            message: `Actual delivery for ${deliveryShortcode} cannot be set until actual pickup for ${missingPickup} has been entered.`
          }
        ]
      }

      const associatedPickups = pickupShortcodes.map(shortcode => ({
        shortcode,
        timestamp: this.parseTimestamp(pickups.get(shortcode)?.value ?? '') as TimestampValue
      }))
      const latestPickup = associatedPickups.reduce((latest, pickup) =>
        pickup.timestamp.date > latest.timestamp.date ? pickup : latest
      )

      if (delivery.date < latestPickup.timestamp.date) {
        return [
          {
            deliveryShortcode,
            message: `Actual delivery for ${deliveryShortcode} cannot be before actual pickup for ${latestPickup.shortcode} (${latestPickup.timestamp.formatted}).`
          }
        ]
      }

      if (delivery.date.getTime() > Date.now()) {
        return [
          {
            deliveryShortcode,
            message: `Actual delivery for ${deliveryShortcode} cannot be in the future (entered ${delivery.formatted}).`
          }
        ]
      }

      return []
    })
  }

  private timestampFields(targets: HTMLInputElement[]): HTMLInputElement[] {
    return targets.filter(target => target.type === 'hidden')
  }

  private timestampFieldsByShortcode(
    targets: HTMLInputElement[],
    shortcodeKey: 'pickupShortcode' | 'deliveryShortcode'
  ): Map<string, HTMLInputElement> {
    return this.timestampFields(targets).reduce((fields, field) => {
      const shortcode = field.dataset[shortcodeKey]
      const existingField = shortcode == null ? undefined : fields.get(shortcode)

      if (
        shortcode != null &&
        shortcode !== '' &&
        (existingField == null || (existingField.name === '' && field.name !== ''))
      ) {
        fields.set(shortcode, field)
      }

      return fields
    }, new Map<string, HTMLInputElement>())
  }

  private parseTimestamp(value: string): TimestampValue | null {
    if (!TIMESTAMP_PATTERN.test(value)) {
      return null
    }

    const timestamp = dayjs.tz(value, this.timeZoneValue)

    if (!timestamp.isValid()) {
      return null
    }

    return { date: timestamp.toDate(), formatted: value }
  }

  private renderErrors(errors: DeliveryValidationError[]): void {
    const messages = new Map(errors.map(error => [error.deliveryShortcode, error.message]))

    this.deliveryErrorTargets.forEach(errorTarget => {
      const message = messages.get(errorTarget.dataset.deliveryShortcode ?? '')
      errorTarget.textContent = message ?? ''
      errorTarget.classList.toggle('hidden', message == null)
    })
  }

  private lateDeliveryConfirmationMessage(): string | null {
    const lateDeliveries = this.lateDeliveries()
    if (lateDeliveries.length === 0) {
      return null
    }

    const timestampList = lateDeliveries.map(
      ({ deliveryShortcode, delivery, pickupShortcode, pickup }) =>
        `${deliveryShortcode}: pickup ${pickupShortcode} (${pickup.formatted}), delivery ${delivery.formatted}`
    )

    return [
      'The following actual deliveries are more than seven days after pickup:',
      timestampList.join('; '),
      'Type confirm to continue.'
    ].join(' ')
  }

  private lateDeliveries(): LateDelivery[] {
    const pickups = this.timestampFieldsByShortcode(this.pickupTargets, 'pickupShortcode')

    return this.timestampFields(this.deliveryTargets).flatMap(deliveryField => {
      const deliveryShortcode = deliveryField.dataset.deliveryShortcode
      const delivery = this.parseTimestamp(deliveryField.value)
      if (deliveryShortcode == null || deliveryShortcode === '' || delivery == null) {
        return []
      }

      return (this.associationsValue[deliveryShortcode] ?? []).flatMap(pickupShortcode => {
        const pickup = this.parseTimestamp(pickups.get(pickupShortcode)?.value ?? '')
        if (
          pickup == null ||
          delivery.date.getTime() - pickup.date.getTime() <= SEVEN_DAYS_IN_MILLISECONDS
        ) {
          return []
        }

        return [{ deliveryShortcode, delivery, pickupShortcode, pickup }]
      })
    })
  }

  private renderLateDeliveryConfirmation(message: string): void {
    this.lateConfirmationMessageTarget.textContent = message
  }

  private isConfirmationSubmission(event: SubmitEvent): boolean {
    return (
      event.submitter instanceof HTMLElement &&
      event.submitter.closest('[data-controller~="ui-button-confirmation"]') != null
    )
  }

  private prevent(event: Event): void {
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  private cancelConfirmLeave(): void {
    const currentWindow = window as typeof window & { cancelConfirmLeave?: () => void }
    currentWindow.cancelConfirmLeave?.()
  }
}
